import React, { useState } from 'react';
import { Nav, NavItem, Row, Col, TabPane, TabContent } from 'reactstrap';
import { NavLink, useLocation } from "react-router-dom";
import './MainNavBar.scss';
import { TABS, SUBTABS } from '../../Views/ConstPaths.js';

//Main tab navigation with tabs and subnav: *** building this first hardwired, not using MainNavData object yet.
// Includes local UI state in order to change the active tab based on user clicks.
// Currently takes *** no props *** but that will likely change.
//
//Bootstrap notes:
// The first row is the toplevel nav (the tabs)
//   For medium+ screen sizes, set up a blank 2-column space with "col-md-2 d-none d-md-block"
//   Then the tabs take up the rest of the space.
// The second row is the subnav (depending on which toplevel tab is selected)
// "offset-md-2": push the main nav 2 cols to the right, for medium+ screen widths. So it lines up with MainSection.
//className notes: syntax from https://medium.com/2359media/why-react-classnames-is-a-lifesaver-and-how-you-should-use-it-if-you-arent-already-b25a44ed5603
//sets <a class="active"> on the link that's clicked. (This requires an activeTab State variable.)
//could add the classnames JS utility to use syntax like this instead: className={classnames({ active: activeTab === '1' })}
//
//The "d-none" <span> elements allow us to hide some words on smaller screen widths, so the tabs can fit on smaller screens.
// 
//For backtick syntax info: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals
//It's just a way to embed an expression within a string.
//

//Tab numbers for each tab:
const NUM = {
  [TABS.GATHER]: '1',
  [TABS.EVAL]: '2',
  [TABS.TEST]: '3',
  [TABS.REVIEW]: '4'
}
//Get the matching tab number for the current URL
function getTabNumber(location){
  const pathname="/"+location.pathname.split("/")[1];
  if (!pathname) return(NUM[TABS.GATHER]);
  else {
    switch (pathname) {
      case TABS.GATHER:
        return(NUM[TABS.GATHER]);
      case TABS.EVAL:
        return(NUM[TABS.EVAL]);
      case TABS.TEST:
        return(NUM[TABS.TEST]);
      case TABS.REVIEW:
        return(NUM[TABS.REVIEW]);
      default:
        return(NUM[TABS.GATHER]);
    }
  }
}
function MainNavBar() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(getTabNumber(location));
  const toggleActiveTab = tab => {
    if(activeTab !== tab) setActiveTab(tab);
  }
  //Calls setActiveTab whenever the URL location changes
  React.useEffect(() => {
    setActiveTab(getTabNumber(location));
  }, [location]);

  const curURL = new URL(window.location.href);

  return (
    <div className="container-fluid" role="navigation">
      <Row className="main-tabs">
        <div className="col-lg-2 d-none d-lg-block"></div>
        <div className="col">
          <Nav tabs className="border-bottom-0">
            <NavItem>
              <NavLink to={TABS.GATHER + curURL.search}
                className={`nav-link ${activeTab === NUM[TABS.GATHER] ? "active" : null}`}
                onClick={() => {toggleActiveTab(NUM[TABS.GATHER]);}}
              > 
                <span className="d-none d-sm-inline">Gather </span><span className="d-none d-md-inline">Background </span>Info
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink to={TABS.EVAL + curURL.search}
                className={`nav-link ${activeTab === NUM[TABS.EVAL] ? "active" : null}`}
                onClick={() => {toggleActiveTab(NUM[TABS.EVAL]);}}
              >
                <span className="d-none d-md-inline">Evaluate </span>Abilities
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink to={TABS.TEST + curURL.search}
                className={`nav-link ${activeTab === NUM[TABS.TEST] ? "active" : null}`}
                onClick={() => {toggleActiveTab(NUM[TABS.TEST]);}}
              >
                Test-drive<span className="d-none d-md-inline"> Solution</span>s
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink to={TABS.REVIEW + curURL.search}
                className={`nav-link ${activeTab === NUM[TABS.REVIEW] ? "active" : null}`}
                onClick={() => {toggleActiveTab(NUM[TABS.REVIEW]);}}
              >
                Review
              </NavLink>
            </NavItem>
          </Nav>
        </div>
      </Row>
      <Row className="border-bottom sub-nav">
        <div className="col offset-lg-2">
          <TabContent activeTab={activeTab}>
            <TabPane tabId={NUM[TABS.GATHER]}>
              <Nav>
                <NavItem>
                  <NavLink 
                    to={SUBTABS.GOALS + curURL.search} 
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.GOALS));
                    }}
                  >
                    Goals & Needs
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink 
                    to={SUBTABS.CONTEXTS + curURL.search} 
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.CONTEXTS));
                    }}
                  >
                    Contexts
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink 
                    to={SUBTABS.HISTORY + curURL.search}
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.HISTORY));
                    }}
                  >
                    History
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink 
                    to={SUBTABS.GATHER_REVIEW + curURL.search}
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.GATHER_REVIEW));
                    }}
                  >
                    Review
                  </NavLink>
                </NavItem>
              </Nav>
            </TabPane>
            <TabPane tabId={NUM[TABS.EVAL]}>
              <Nav>
                <NavItem>
                  <NavLink 
                    to={SUBTABS.PHYSICAL + curURL.search} 
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.PHYSICAL));
                    }}
                  >
                    Physical
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to={SUBTABS.VISUAL + curURL.search} className="nav-link">Visual</NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to={SUBTABS.COGNITION + curURL.search} className="nav-link">Language & Cognition</NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to={SUBTABS.OTHER + curURL.search} className="nav-link">Other</NavLink>
                </NavItem>
                <NavItem>
                  <NavLink 
                    to={SUBTABS.EVAL_REVIEW + curURL.search}
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.EVAL_REVIEW));
                    }}
                  >
                    Review
                  </NavLink>
                </NavItem>
              </Nav>
            </TabPane>
            <TabPane tabId={NUM[TABS.TEST]}>
              <Nav>
                <NavItem>
                  <NavLink
                    to={SUBTABS.CHOOSE + curURL.search} 
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.CHOOSE));
                    }}
                  >
                    Choose it
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    to={SUBTABS.SETUP + curURL.search} 
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.SETUP));
                    }}                  
                  >
                    Set it up
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    to={SUBTABS.TRY + curURL.search} 
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.TRY));
                    }}
                  >
                    Try it
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    to={SUBTABS.RATE + curURL.search} 
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.RATE));
                    }}
                  >
                    Rate it
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    to={SUBTABS.INTERPRET + curURL.search} 
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.INTERPRET));
                    }}
                  >
                    Interpret it
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    to={SUBTABS.TEST_REVIEW + curURL.search} 
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.TEST_REVIEW));
                    }}
                  >
                    Review
                  </NavLink>
                </NavItem>
              </Nav>
            </TabPane>
            <TabPane tabId={NUM[TABS.REVIEW]}>
              <Nav>
                <NavItem>
                  <NavLink
                    to={SUBTABS.COMPARE + curURL.search} 
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.COMPARE));
                    }}
                  >
                    Compare
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    to={SUBTABS.MAKE_CHOICES + curURL.search} 
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.MAKE_CHOICES));
                    }}
                  >
                    Make some choices
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    to={SUBTABS.NEXT_STEPS + curURL.search} 
                    className="nav-link"
                    isActive={(match, location) => {
                      return(location.pathname.includes(SUBTABS.NEXT_STEPS));
                    }}
                  >
                    Next steps
                  </NavLink>
                </NavItem>
              </Nav>
            </TabPane>
          </TabContent>
        </div>
      </Row>
    </div>
  );
}

export default MainNavBar;